import { useEffect, useRef, useState, useCallback } from "react";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { StyledSelect } from "../../StyledCss/styledInputs";
import "./index.css";

const CustomMultiSelect = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "---Select---",
  idKey = "id",
  nameKey = "name",
  showSearch = false,
  noOptionsMessage = "No options available",
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => {
      if (!prev && showSearch) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
      return !prev;
    });
  }, [showSearch]);

  const handleCheckboxChange = useCallback(
    (e) => {
      const value = e.target.value;
      const newValues = selectedValues.includes(value)
        ? selectedValues.filter((id) => id !== value)
        : [...selectedValues, value];
      onChange(newValues);
    },
    [selectedValues, onChange]
  );

  const getDisplayText = useCallback(() => {
    if (selectedValues.length === 0) return placeholder;
    return options
      .filter((option) => selectedValues.includes(option[idKey]))
      .map((option) => option[nameKey])
      .join(", ");
  }, [selectedValues, options, placeholder, idKey, nameKey]);

  const filteredOptions = showSearch
    ? options.filter((option) =>
        option[nameKey].toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ width: "100%" }}>
      <StyledSelect
        as="button"
        type="button"
        onClick={toggleDropdown}
        style={{
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingLeft: "10px",
          paddingRight: "10px",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getDisplayText()}
        </span>
        <MdOutlineKeyboardArrowDown />
      </StyledSelect>
      <div
        className="custom-multiselect-dropdown shadow"
        style={{
          display: isDropdownOpen ? "block" : "none",
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: "white",
        }}
      >
        {showSearch && options.length > 0 && (
          <div className="search-container">
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div className="options-container">
          {options.length === 0 ? (
            <li className="no-results">{noOptionsMessage}</li>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <li key={option[idKey]}>
                <label>
                  <input
                    onChange={handleCheckboxChange}
                    value={option[idKey]}
                    type="checkbox"
                    checked={selectedValues.includes(option[idKey])}
                  />{" "}
                  {option[nameKey]}
                </label>
              </li>
            ))
          ) : (
            <li className="no-results">No results found</li>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomMultiSelect;
